import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, Briefcase, Trophy, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mockB2BOffers } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import FeatureCard from '../components/FeatureCard';
import CtaBanner from '../components/CtaBanner';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import { useBrandName } from '../context/SettingsContext';
import EditableText from '../components/EditableText';
import { BTN_PRIMARY } from '../config/brand';
import { IMAGES } from '../config/images';

const B2B_BENEFITS = [
  {
    icon: Trophy,
    variant: 'purple',
    title: 'Expérience unique',
    description: 'Une activité originale qui marque les esprits',
  },
  {
    icon: Users,
    variant: 'orange',
    title: "Cohésion d'équipe",
    description: 'Renforce la collaboration et la communication',
  },
  {
    icon: Briefcase,
    variant: 'purple',
    title: 'Sur mesure',
    description: 'Solutions adaptées à vos besoins et objectifs',
  },
  {
    icon: Building2,
    variant: 'orange',
    title: 'Dans vos locaux',
    description: 'Nous venons chez vous ou dans nos centres',
  },
];

const PARTNER_LOGOS = [
  { src: IMAGES.partners.algora, alt: 'Algora' },
  { src: IMAGES.partners.eibNice, alt: 'EIB Nice' },
  { src: IMAGES.partners.eibCagnes, alt: 'EIB Cagnes' },
  { src: IMAGES.partners.eibPegomas, alt: 'EIB Pégomas' },
  { src: IMAGES.partners.eibCil, alt: 'EIB CIL' },
  { src: IMAGES.partners.cjps, alt: 'CJPS' },
].filter((p) => p.src);

const Entreprises = () => {
  const brandName = useBrandName();
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        eyebrow="B2B"
        eyebrowKey="entreprises.hero.eyebrow"
        title="Solutions pour entreprises & écoles"
        titleKey="entreprises.hero.title"
        subtitle="Team building, formations professionnelles et partenariats pédagogiques sur mesure."
        subtitleKey="entreprises.hero.subtitle"
      />

      <CircuitDivider className="mb-0" />

      <section className="py-14 sm:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Offres"
            eyebrowKey="entreprises.offers.eyebrow"
            title="Nos formules B2B"
            titleKey="entreprises.offers.title"
            subtitle="Team building, formations et partenariats adaptés à votre organisation."
            subtitleKey="entreprises.offers.subtitle"
            reveal
          />

          <div className="grid lg:grid-cols-3 gap-8">
            {mockB2BOffers.map((offer, i) => (
              <Reveal key={offer.id} animation="fade-up" delay={i * 100}>
                <article className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col h-full">
                  <div className="h-44 sm:h-48 overflow-hidden bg-slate-50">
                    <img
                      src={offer.image}
                      alt={offer.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1 space-y-4">
                    <div>
                      <h3 className="text-slate-900 text-xl font-semibold mb-2 tracking-tight">
                        {offer.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{offer.description}</p>
                    </div>

                    <dl className="space-y-2 text-sm text-slate-600">
                      <div className="flex items-center justify-between gap-3">
                        <dt>Durée</dt>
                        <dd className="text-slate-900 font-medium">{offer.duration}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt>Participants</dt>
                        <dd className="text-slate-900 font-medium">{offer.participants}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt>Prix</dt>
                        <dd className="text-slate-900 font-medium">{offer.price}</dd>
                      </div>
                    </dl>

                    <div className="pt-4 border-t border-slate-200 flex-1">
                      <h4 className="text-slate-900 font-semibold mb-2 text-sm">Bénéfices</h4>
                      <ul className="space-y-1.5">
                        {offer.benefits.map((benefit, j) => (
                          <li key={j} className="flex items-start text-xs text-slate-600">
                            <CheckCircle2
                              className="h-3 w-3 text-[#3a00e0] mr-1.5 shrink-0 mt-0.5"
                              strokeWidth={1.75}
                              aria-hidden="true"
                            />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link to="/contact?subject=devis-entreprise" className="block">
                      <Button className={`w-full ${BTN_PRIMARY}`}>
                        <EditableText editKey="entreprises.card.cta" defaultValue="Demander un devis" as="span" />
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Nos atouts"
            eyebrowKey="entreprises.why.eyebrow"
            title={`Pourquoi choisir ${brandName} ?`}
            titleKey="entreprises.why.title"
            reveal
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {B2B_BENEFITS.map((item, i) => (
              <Reveal key={item.title} animation="fade-up" delay={i * 60}>
                <FeatureCard
                  {...item}
                  titleKey={`entreprises.why.${i + 1}.title`}
                  descriptionKey={`entreprises.why.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {PARTNER_LOGOS.length > 0 && (
        <section className="py-16 sm:py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              eyebrow="Références"
              eyebrowKey="entreprises.references.eyebrow"
              title="Ils nous font confiance"
              titleKey="entreprises.references.title"
              subtitle="Écoles et partenaires pédagogiques"
              subtitleKey="entreprises.references.subtitle"
              reveal
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6 sm:gap-8 items-center">
              {PARTNER_LOGOS.map((partner, i) => (
                <Reveal key={partner.alt} animation="fade-in" delay={i * 40}>
                  <div className="flex items-center justify-center min-h-[4rem] px-2">
                    <img
                      src={partner.src}
                      alt={partner.alt}
                      className="max-h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity grayscale hover:grayscale-0"
                      loading="lazy"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBanner
        className="bg-slate-50"
        title="Discutons de votre projet"
        titleKey="entreprises.cta.title"
        subtitle="Contactez-nous pour recevoir un devis personnalisé et organiser votre événement"
        subtitleKey="entreprises.cta.subtitle"
      >
        <Link to="/contact?subject=devis-entreprise">
          <Button size="lg" className="bg-white text-[#3a00e0] hover:bg-[rgba(58,0,224,0.06)]">
            <EditableText editKey="entreprises.cta.button" defaultValue="Demander un devis" as="span" />
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </CtaBanner>

      <Footer />
    </div>
  );
};

export default Entreprises;
